import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import * as session from '../auth/session';

// Auth Phase 1 (P1-B). The token lives in src/auth/session.js, never here.
// On load: session.bootstrap() decides v1/v2 from /health, exchanges a legacy
// localStorage token for a v2 session (deleting the key), or resumes from the
// httpOnly refresh cookie; then /api/auth/me populates `user`.
//
// sessionLost: set when a refresh is refused (revoked / deactivated / expired)
// or a socket closes with 4401. The page stays mounted underneath —
// SessionLostModal overlays it with an inline sign-in; a successful sign-in
// clears the flag and the page resumes with its state intact. No hard
// navigation anywhere.

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionLost, setSessionLost] = useState(null); // null | { code, message }
  // P2-A (R6): the server requires a new password before anything else.
  // ProtectedRoute swaps the app for the full-screen ForcedPasswordReset view.
  const [passwordResetRequired, setPasswordResetRequired] = useState(false);

  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      return true;
    } catch (error) {
      console.error('Auth error:', error);
      return false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const signedIn = await session.bootstrap();
      if (signedIn) await fetchUser();
      if (!cancelled) { setPasswordResetRequired(session.isPasswordResetRequired()); setLoading(false); }
    })();
    const offLost = session.onSessionLost((detail) => { if (!cancelled) setSessionLost(detail || { code: 'SESSION_LOST' }); });
    // Another tab signed in (cross-tab sync): this tab resumed from the new cookie.
    const offRestored = session.onSessionRestored((detail) => {
      if (cancelled || detail?.via !== 'other-tab') return;
      fetchUser().then((ok) => { if (ok && !cancelled) setSessionLost(null); });
    });
    const offReset = session.onPasswordResetRequired((detail) => { if (!cancelled) setPasswordResetRequired(!!detail?.required); });
    return () => { cancelled = true; offLost(); offRestored(); offReset(); };
  }, [fetchUser]);

  // P2-A: after a successful change-password the token still carries
  // pwd_reset — refresh mints clean claims (the server re-reads the row), which
  // clears the flag inside the session module; then reload the profile.
  const completePasswordReset = async () => {
    await session.refresh();
    await fetchUser();
    setPasswordResetRequired(session.isPasswordResetRequired());
  };

  const login = async (identifier, password) => {
    try {
      await session.login(identifier, password);
      await fetchUser();
      setSessionLost(null);
      toast.success('Welcome to GVC Portal!');
      return { success: true };
    } catch (error) {
      const message = error.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    await session.logout();
    setUser(null);
    setSessionLost(null);
    setPasswordResetRequired(false);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    sessionLost,
    passwordResetRequired,
    completePasswordReset,
    sessionId: session.getSessionId(),
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isManager: user?.role === 'manager',
    isEmployee: user?.role === 'employee'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
